import React, { useEffect } from 'react';
import { useIntl } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';
import { usePathParam } from 'react-resource-router';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import Heading from '@atlaskit/heading';
import { Box, media, xcss } from '@atlaskit/primitives';
import { ExperienceSuccess } from '@atlassian/ufo';
import {
    INITIAL_REQUEST_FORMS_COUNT,
    ResultList,
    sendSearchOperationalAnalyticEvents,
    sendSearchUIAnalyticEvents,
} from '../common';
import { searchExperience } from '../experiences';
import { RequestFormItem } from '../request-form-item';
import type { requestFormsFragment$key } from './__generated__/requestFormsFragment.graphql';
import messages from './messages';

export interface Props {
    term: string;
    result: requestFormsFragment$key;
    updateResultsCount: (count: number, acknowledgement?: number) => void;
    createAnalyticsEvent?: CreateUIAnalyticsEvent;
}

export const RequestForms = ({ result, term, updateResultsCount, createAnalyticsEvent }: Props) => {
    const { formatMessage } = useIntl();
    const data = useFragment<requestFormsFragment$key>(
        graphql`
            fragment requestFormsFragment on HelpObjectStoreQueryApi {
                requestForms: searchHelpObjects(
                    searchInput: {
                        cloudId: $cloudId
                        queryTerm: $queryTerm
                        resultLimit: $resultLimit
                        entityType: REQUEST_FORM
                        portalIds: $portalIds
                        helpCenterAri: $helpCenterAri
                    }
                ) @required(action: THROW) {
                    ... on HelpObjectStoreSearchResult {
                        __typename @required(action: THROW)
                        id @required(action: THROW)
                        isExternal @required(action: THROW)
                        searchBackend
                        ...requestFormItemFragment
                    }

                    ... on HelpObjectStoreQueryError {
                        message
                    }
                }
            }
        `,
        result
    );
    const [portalId] = usePathParam('portalId');

    const slicedRequestForms = data.requestForms.slice(0, INITIAL_REQUEST_FORMS_COUNT);
    const showSection = slicedRequestForms.length > 0;

    useEffect(() => {
        let externalResourcesCount = 0;
        let firstPartySearchPlatformResourcesCount = 0;
        let hasError = false;
        let errorMessage: string | undefined;

        slicedRequestForms.forEach((resource) => {
            if (resource) {
                const { __typename, isExternal, searchBackend, message } = resource;

                if (__typename === 'HelpObjectStoreSearchResult') {
                    if (isExternal) {
                        externalResourcesCount += 1;
                    }
                    if (searchBackend === 'SEARCH_PLATFORM') {
                        firstPartySearchPlatformResourcesCount += 1;
                    }
                    if (message && !errorMessage) {
                        hasError = true;
                        errorMessage = message;
                    }
                }
            }
        });

        if (showSection && hasError) {
            sendSearchOperationalAnalyticEvents(
                { hasError, errorMessage, action: 'requestTypeSearchFailed' },
                createAnalyticsEvent,
                portalId
            );
        }

        if (showSection && !hasError) {
            sendSearchUIAnalyticEvents(
                {
                    externalResourcesCount,
                    firstPartySearchPlatformResourcesCount,
                    action: 'rendered',
                    resourceType: 'request-form',
                    resourceCount: slicedRequestForms.length,
                },
                createAnalyticsEvent,
                portalId
            );

            sendSearchOperationalAnalyticEvents(
                { action: 'requestTypeSearchSucceeded' },
                createAnalyticsEvent,
                portalId
            );
        }
        // adding slicedRequestForms to deps array causes multiple 'rendered' events to be fired
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [term, showSection, slicedRequestForms.length, createAnalyticsEvent, portalId]);

    useEffect(() => {
        updateResultsCount(slicedRequestForms.length, 1);
    }, [term, slicedRequestForms.length, updateResultsCount]);

    return (
        <>
            {showSection && (
                <Box xcss={containerStyles}>
                    <Box xcss={headingStyles}>
                        <Heading size="xxsmall">{formatMessage(messages.requestForms)}</Heading>
                    </Box>
                    <ResultList>
                        {slicedRequestForms.map((resource, index) => {
                            if (resource?.__typename === 'HelpObjectStoreSearchResult') {
                                const itemOrder = index + 1;
                                const key = resource.id;

                                return (
                                    <li key={key}>
                                        <RequestFormItem itemOrder={itemOrder} term={term} result={resource} />
                                    </li>
                                );
                            }
                            return null;
                        })}
                    </ResultList>
                </Box>
            )}
            <ExperienceSuccess experience={searchExperience} />
        </>
    );
};

const containerStyles = xcss({
    overflow: 'hidden',
    paddingTop: 'space.250',
    paddingRight: 'space.150',
    paddingBottom: 'space.150',
    paddingLeft: 'space.150',
    gap: 'space.150',
    borderColor: 'color.border',
    borderStyle: 'solid',
    borderRadius: 'radius.small',
    borderWidth: 'border.width',
    rowGap: 'space.100',
    [media.above.xxs]: { marginLeft: 'space.100', marginRight: 'space.100', width: '1fr' },
    [media.above.md]: {
        marginLeft: 'space.0',
        marginRight: 'space.0',
        width: '320px',
    },
});
const headingStyles = xcss({
    width: '100%',
    paddingRight: 'space.100',
    paddingBottom: 'space.050',
    paddingLeft: 'space.100',
});

export default withAnalyticsEvents()(RequestForms);
